import React, { useEffect, useState } from "react";
import { Link, useHistory, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import { Collapse, Nav } from "react-bootstrap";
import { Avatar } from "@material-ui/core";
import useStyles from "./sidebarStyles";
import { getUserFromLocalStorage } from "../../utilities/user.js";
import useAuth from "../../hooks/useAuth";
// For BRAND_OWNER accounts we prefer to show the brand's tradingName
// in the sidebar (e.g. "Nile Chocolates") rather than the operator's
// personal name. The hook returns `{ brandName: null }` for every
// other account type so the existing name-fallback chain keeps
// working unchanged.
import useCurrentBrand from "../../hooks/useCurrentBrand";
import { useMenuBadges } from "../../hooks/useMenuBadges";
import { describeBadge } from "../../utilities/menuBadges";
import { useSelector } from "react-redux";
import { useEntitlement } from "../../context/EntitlementContext.js";
import { usePermissions } from "../../utilities/usePermissions.js";
import { normalizeRequiredPermission } from "../../routes.js";
import { useSellerReviewQueue } from "../../hooks/useSellerReviewQueue.js";
import ChangePasswordDialog from "../Account/ChangePasswordDialog";
import * as impersonation from "../../utilities/impersonation.js";

function Sidebar({ routes, image, background }) {
  const classes = useStyles();
  let location = useLocation();
  const history = useHistory();

  const [user, setUser] = useState(getUserFromLocalStorage());
  const [userCollapseState, setUserCollapseState] = useState(false);
  const [state, setState] = useState({});
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);

  const userRole       = user?.result?.user_role;
  const userType       = user?.result?.userType;
  const operationMode  = user?.result?.operationMode;
  const isLusukuStaff  = userType === "LUSUKU STAFF";
  // Own-password change is for a platform Users login. 3PL partners and their
  // staff sign in against their own collections (and change it on the partner
  // profile), and an admin signed in as a vendor must not set the vendor's.
  const canChangeOwnPassword =
    operationMode !== "3PL_PARTNER" &&
    operationMode !== "3PL_STAFF" &&
    !impersonation.active();

  // Brand vendors get their tradingName resolved via the hook (one
  // cached API call per session). `brandName` + `brandLogoUrl` are
  // null for non-brand accounts — callers must treat them as optional.
  // `brandLogoUrl` is the Brand model's `imageUrl` field (the logo
  // uploaded on the Edit Brand form), so the sidebar chip mirrors the
  // badge that appears on the brand's storefront.
  const { brandName, brandLogoUrl } = useCurrentBrand();

  // Single source of truth for the name shown in the sidebar (avatar
  // initial + user chip label). Brand vendors see their brand; anyone
  // else falls back to the existing chain, ending in "Guest".
  const displayName =
    brandName ||
    user?.result?.name ||
    user?.result?.companyName ||
    user?.result?.businessName ||
    "Guest";
  const canPlaceInHouse = isLusukuStaff
    ? (userRole === "SUPERUSER" || userRole === "SUPERVISOR")
    : (operationMode === "RESTAURANT" || operationMode === "RETAILER" ||
       operationMode === "WHOLESALER" || operationMode === "HEALTHCARE" ||
       operationMode === "ONLINE" || operationMode === "BRAND_OWNER" ||
       userType === "SELLER");

  const handleInHouseClick = () => {
    window.dispatchEvent(new CustomEvent("openInHouseOrderModal"));
  };
  // console.log("Encrypted user Sidebar:", localStorage.getItem("profile"));
  // console.log("Token sidebar:", localStorage.getItem("token"));
  // console.log(restaurantProfile, "restaurantProfile in Sidebar")

  const { profile: retailerWholesalerProfile } = useSelector(
    (state) => state.retailerWholesalerProfile
  );
  const { profile: restaurantProfile } = useSelector(
    (state) => state.restaurantProfile
  );

  // The logo for this account, resolved once. Brand vendors get the brand
  // logo so the chip matches their storefront badge; a 3PL partner's logo
  // arrives as `imageUrl` on the login profile; everyone else falls through
  // the existing chain. Null means there is no artwork, and the chip below
  // renders the initial instead.
  const avatarSrc =
    brandLogoUrl ||
    user?.result?.imageUrl ||
    restaurantProfile?.imageUrl ||
    retailerWholesalerProfile?.imageUrl ||
    null;

  useAuth(true); // Ensure user is authenticated before rendering the sidebar

  // Phase 8 — runtime entitlement gate. Platform admins bypass, so their
  // sidebar is unchanged. Vendor owners + sub-staff have per-module verdicts.
  // A route is hidden from the menu when its entitlementKey is denied, BUT
  // the route is still registered with React Router so direct navigation
  // lands on an EntitlementGate empty-state instead of a 404.
  const { isEntitled, bypassesEntitlements, loading: entitlementLoading } = useEntitlement();
  const isEntitledRoute = (prop) => {
    if (!prop?.entitlementKey) return true; // unkeyed routes never gated
    if (bypassesEntitlements) return true;
    if (entitlementLoading) return true; // don't blink during initial fetch
    return isEntitled(prop.entitlementKey);
  };

  // RBAC permission gate — independent of the Phase-8 entitlement gate above.
  // A menu item carrying `requiredPermission` (a "domain:action" string OR a
  // legacy { key, level } object) is hidden when can() is definitively false.
  // SUPERUSER bypasses and we fail OPEN while permissions load (see
  // usePermissions), so the menu never flash-hides during the initial fetch.
  // Routes without `requiredPermission` are always visible (resilient default).
  const { can } = usePermissions();
  const isPermittedRoute = (prop) => {
    const key = normalizeRequiredPermission(prop?.requiredPermission);
    if (!key) return true; // unkeyed routes are never permission-gated
    return can(key);
  };

  // Live counts for menu items that declare `liveBadge` in routes.js.
  //
  // The count, the tone and the sentence a screen reader announces all come
  // from utilities/menuBadges, so adding a badge is naming a key here and
  // publishing a number from wherever the data already lives. This used to be
  // a one-entry object with "awaiting verification" hardcoded into the markup,
  // which was correct for exactly one badge and wrong for any second one.
  //
  // The seller queue keeps its own store (it predates this and is fed over its
  // own socket room), so it is bridged in rather than migrated.
  const sellerQueue = useSellerReviewQueue();
  const badgeCounts = useMenuBadges();
  const allCounts = { ...badgeCounts, pendingSellers: sellerQueue.pending || 0 };

  const renderLiveBadge = (prop) => {
    const badge = prop?.liveBadge ? describeBadge(prop.liveBadge, allCounts) : null;
    if (!badge) return null;
    return (
      <span
        className={`sidebar-live-badge sidebar-live-badge--${badge.tone}`}
        title={badge.label}
        aria-label={badge.label}
      >
        {badge.display}
      </span>
    );
  };

  /**
   * The same badge for a row that renders its label in a <p> (anything with an
   * icon, including every collapse header). Without this a `liveBadge` on a
   * group silently rendered nothing, so the count only worked on the icon-less
   * leaf rows and there was no way to tell a group "there is work inside me".
   */
  const renderGroupBadge = (prop) => {
    const badge = prop?.liveBadge ? describeBadge(prop.liveBadge, allCounts) : null;
    if (!badge) return null;
    return (
      <span
        className={`sidebar-live-badge sidebar-live-badge--${badge.tone} sidebar-live-badge--group`}
        title={badge.label}
        aria-label={badge.label}
      >
        {badge.display}
      </span>
    );
  };

  useEffect(() => {
    setState(getCollapseStates(routes));
  }, [routes]);

  useEffect(() => {
    setUser(getUserFromLocalStorage());
  }, [location]);

  const getCollapseStates = (routes) => {
    if (!routes) {
      return {};
    }
    let initialState = {};
    routes.map((prop) => {
      if (prop.hidden) {
        return null;
      }
      if (prop.collapse) {
        initialState = {
          [prop.state]: getCollapseInitialState(prop.views),
          ...getCollapseStates(prop.views),
          ...initialState,
        };
      }
      return null;
    });
    return initialState;
  };

  const getCollapseInitialState = (routes) => {
    for (let i = 0; i < routes.length; i++) {
      if (routes[i].collapse && getCollapseInitialState(routes[i].views)) {
        return true;
      } else if (location.pathname === routes[i].layout + routes[i].path) {
        return true;
      }
    }
    return false;
  };

  const createLinks = (routes) => {
    if (!routes) {
      return null;
    }
    const hasVisibleDescendant = (items) =>
      (items || []).some((item) => {
        if (item.redirect || item.hidden) return false;
        if (!isEntitledRoute(item) || !isPermittedRoute(item)) return false;
        return item.collapse ? hasVisibleDescendant(item.views) : true;
      });

    return routes.map((prop, key) => {
      if (prop.redirect || prop.hidden) {
        return null;
      }
      // Phase 8 — skip menu items whose per-vendor entitlement is denied.
      if (!isEntitledRoute(prop)) {
        return null;
      }
      // RBAC — skip menu items the current user lacks permission for. Because
      // createLinks recurses into prop.views, gating each prop here covers both
      // top-level groups and their nested sub-views in one place.
      if (!isPermittedRoute(prop)) {
        return null;
      }
      if (prop.collapse && !hasVisibleDescendant(prop.views)) {
        return null;
      }
      if (prop.collapse) {
        var st = {};
        st[prop["state"]] = !state[prop.state];
        return (
          <Nav.Item
            className={getCollapseInitialState(prop.views) ? "active" : ""}
            as="li"
            key={key}
          >
            <Nav.Link
              className={state[prop.state] ? "collapsed" : ""}
              data-toggle="collapse"
              onClick={(e) => {
                e.preventDefault();
                setState({ ...state, ...st });
              }}
              aria-expanded={state[prop.state]}
            >
              {prop.iconComponent ? (
                <span
                  className="sidebar-icon-component"
                  style={prop.iconColor ? { color: prop.iconColor } : undefined}
                >
                  {prop.iconComponent}
                </span>
              ) : (
                <i
                  className={prop.icon}
                  style={prop.iconColor ? { color: prop.iconColor } : undefined}
                ></i>
              )}
              <p>
                {prop.name} {renderGroupBadge(prop)} <b className="caret"></b>
              </p>
            </Nav.Link>
            <Collapse in={state[prop.state]}>
              <div>
                <Nav as="ul">{createLinks(prop.views)}</Nav>
              </div>
            </Collapse>
          </Nav.Item>
        );
      }
      return (
        <Nav.Item
          className={activeRoute(prop.layout + prop.path)}
          key={key}
          as="li"
        >
          <Nav.Link to={prop.layout + prop.path} as={Link}>
            {prop.iconComponent ? (
              <>
                <span
                  className="sidebar-icon-component"
                  style={prop.iconColor ? { color: prop.iconColor } : undefined}
                >
                  {prop.iconComponent}
                </span>
                <p>
                  {prop.name}
                  {renderGroupBadge(prop)}
                </p>
              </>
            ) : prop.icon ? (
              <>
                <i
                  className={prop.icon}
                  style={prop.iconColor ? { color: prop.iconColor } : undefined}
                />
                <p>
                  {prop.name}
                  {renderGroupBadge(prop)}
                </p>
              </>
            ) : (
              <>
                <span className="sidebar-mini">{prop.mini}</span>
                <span className="sidebar-normal">
                  {prop.name}
                  {renderLiveBadge(prop)}
                </span>
              </>
            )}
          </Nav.Link>
        </Nav.Item>
      );
    });
  };

  const activeRoute = (routeName) => {
    return location.pathname === routeName ? "active" : "";
  };

  const profileTab = (e) => {
    e.preventDefault();
    const operationMode = user?.result?.operationMode;
    if (operationMode === "HEALTHCARE") {
      history.push("/admin/healthcare-account");
    } else if (operationMode === "3PL_PARTNER") {
      history.push("/admin/partner-profile");
    } else {
      history.push("/admin/profile");
    }
  };

  return (
    <>
      <div className="sidebar" data-color={background} data-image={image}>
        <div className="sidebar-wrapper">
          <button type="button" className="lk-mobile-nav-close" aria-label="Close navigation" onClick={() => document.documentElement.classList.remove("nav-open")}>×</button>
          <div className="logo">
            <a
              className="simple-text logo-mini"
              href="#"
              style={{ width: "48px", marginLeft: "16px", marginRight: "14px" }}
            >
              <div
                className="logo-img"
                style={{ width: "48px", height: "48px", maxHeight: "none", display: "flex", alignItems: "center", justifyContent: "center" }}
              >
                <img
                  src={require("assets/img/lusuku_logo_main.png")}
                  alt="lusuku_logo"
                  style={{ position: "static", top: "auto", left: "auto", width: "48px", height: "48px", maxWidth: "none", maxHeight: "none", objectFit: "contain" }}
                />
              </div>
            </a>
            <a
              className="simple-text logo-normal pt-3"
              href="#"
              style={{
                textDecoration: "none",
                fontStyle: "normal",
                fontSize: "1.6rem",
                fontFamily: "'Nunito', sans-serif",
                fontWeight: 700,
                letterSpacing: "1px",
              }}
            >
              Lusuku
            </a>
          </div>
          <div className="user">
            <div className="photo">
              {/* "Do we have any name at all?" — driven by the same
                  displayName the avatar initial will actually render,
                  so a brand vendor with no personal `name` field still
                  gets a proper initial avatar (B for Brand) instead of
                  silently falling through to the generic icon. */}
              {displayName && displayName !== "Guest" ? (
                <Avatar
                  // Two different chips share this element. With a logo it is
                  // a logo tile (white, contained, so a wordmark keeps its
                  // ends); with no logo it is the teal initial chip. Picking
                  // the class off `avatarSrc` keeps the white ground away
                  // from the initial, which is drawn in white.
                  className={avatarSrc ? classes.logoAvatar : classes.purple}
                  alt={displayName}
                  // When `src` is null OR the image fails to load,
                  // Material-UI renders the children below (the initial).
                  src={avatarSrc || undefined}
                >
                  {displayName?.charAt(0) || ""}
                </Avatar>
              ) : (
                <Avatar
                  src={
                    require("assets/img/Vendor-Dashboard-Icon.png").default ||
                    restaurantProfile?.imageUrl ||
                    retailerWholesalerProfile?.imageUrl
                  }
                  style={{
                    width: "30px",
                    height: "30px",
                  }}
                />
              )}
            </div>
            <div className="info">
              <p
                className={userCollapseState ? "collapsed" : ""}
                data-toggle="collapse"
                href="#pablo"
                onClick={(e) => {
                  e.preventDefault();
                  setUserCollapseState(!userCollapseState);
                }}
                aria-expanded={userCollapseState}
              >
                <span style={{ textTransform: "capitalize" }}>
                  {displayName}
                  <b className="caret"></b>
                </span>
              </p>
              <Collapse id="collapseExample" in={userCollapseState}>
                <div>
                  <Nav as="ul">
                    <li>
                      <a
                        className="profile-dropdown"
                        href="#profile"
                        onClick={profileTab}
                      >
                        <span className="sidebar-mini">EP</span>
                        <span className="sidebar-normal">Edit Profile</span>
                      </a>
                    </li>
                    {canChangeOwnPassword && (
                      <li>
                        <a
                          className="profile-dropdown"
                          href="#change-password"
                          onClick={(e) => {
                            e.preventDefault();
                            setChangePasswordOpen(true);
                          }}
                        >
                          <span className="sidebar-mini">CP</span>
                          <span className="sidebar-normal">Change Password</span>
                        </a>
                      </li>
                    )}
                    <li
                      style={{
                        paddingLeft: "15px",
                        paddingRight: "15px",
                        marginTop: "5px",
                      }}
                    >
                      <div
                        style={{
                          display: "inline-block",
                          padding: "4px 12px",
                          borderRadius: "12px",
                          fontSize: "11px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                          backgroundColor: isLusukuStaff
                            ? "#3b4f66" // staff slate (role chip, not a vendor mode)
                            : user?.result?.operationMode === "RESTAURANT"
                              ? "#17385aff"
                              : user?.result?.operationMode === "HEALTHCARE"
                                ? "#E91E63"
                                : user?.result?.operationMode === "RETAILER" ||
                                  user?.result?.operationMode === "WHOLESALER" ||
                                  user?.result?.operationMode === "ONLINE"
                                  ? "#FF6B1A"
                                  : user?.result?.operationMode === "3PL_PARTNER" ||
                                    user?.result?.operationMode === "3PL_STAFF"
                                    ? "#006202"
                                    : "#6c757d",
                          color: "white",
                        }}
                      >
                        {/* A staff account is labelled by its ROLE. Its
                            operationMode (if any) is vendor leftovers and
                            showing it (or the "VENDOR" fallback) misidentifies
                            staff as vendors. */}
                        {isLusukuStaff
                          ? `LUSUKU ${userRole || "STAFF"}`
                          : user?.result?.operationMode === "RETAILER"
                          ? "RETAIL"
                          : user?.result?.operationMode === "WHOLESALER"
                            ? "WHOLESALE"
                            : user?.result?.operationMode === "ONLINE"
                              ? "ONLINE RETAIL"
                              : user?.result?.operationMode === "3PL_PARTNER"
                                ? "3PL PARTNER"
                                : user?.result?.operationMode === "3PL_STAFF"
                                  ? "3PL STAFF"
                                  : user?.result?.operationMode || "VENDOR"}
                      </div>
                    </li>
                  </Nav>
                </div>
              </Collapse>
            </div>
          </div>
          {canPlaceInHouse && (
            <div style={{ padding: "10px 15px 4px" }}>
              <button
                onClick={handleInHouseClick}
                style={{
                  width: "100%",
                  padding: "10px 14px",
                  borderRadius: 8,
                  background: "linear-gradient(135deg, #f2c811 0%, #e0b800 100%)",
                  border: "none",
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: "pointer",
                  color: "#0f172a",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 7,
                  boxShadow: "0 2px 10px rgba(242,200,17,0.35)",
                  letterSpacing: "0.3px",
                  transition: "opacity 0.15s",
                }}
                onMouseEnter={e => e.currentTarget.style.opacity = "0.88"}
                onMouseLeave={e => e.currentTarget.style.opacity = "1"}
              >
                <i className="nc-icon nc-simple-add" style={{ fontSize: 11, fontWeight: 900 }} />
                New In-House Order
              </button>
            </div>
          )}
          <Nav as="ul">{createLinks(routes)}</Nav>
        </div>
        <div
          className="sidebar-background"
          style={{
            backgroundImage: "url('" + image + "')",
          }}
        ></div>
      </div>
      {canChangeOwnPassword && (
        <ChangePasswordDialog open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
      )}
    </>
  );
}

let linkPropTypes = {
  path: PropTypes.string,
  layout: PropTypes.string,
  name: PropTypes.string,
  component: PropTypes.oneOfType([PropTypes.func, PropTypes.element]),
};

Sidebar.defaultProps = {
  image: "",
  background: "black",
  routes: [],
};

Sidebar.propTypes = {
  image: PropTypes.string,
  background: PropTypes.oneOf([
    "black",
    "azure",
    "green",
    "orange",
    "red",
    "purple",
  ]),
  routes: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.shape({
        ...linkPropTypes,
        icon: PropTypes.string,
        iconComponent: PropTypes.node,
        iconColor: PropTypes.string,
      }),
      PropTypes.shape({
        collapse: true,
        path: PropTypes.string,
        name: PropTypes.string,
        state: PropTypes.string,
        icon: PropTypes.string,
        iconComponent: PropTypes.node,
        iconColor: PropTypes.string,
        views: PropTypes.shape({
          ...linkPropTypes,
          mini: PropTypes.string,
        }),
      }),
    ])
  ),
};

export default Sidebar;
